<template>
  <div class="blog-post-page">
    <!-- 动态背景 -->
    <div class="animated-background">
      <div class="floating-shapes">
        <div class="shape shape-1"></div>
        <div class="shape shape-2"></div>
      </div>
      <div class="grid-overlay"></div>
    </div>

    <!-- 导航栏 -->
    <nav class="top-nav">
      <div class="nav-container">
        <router-link to="/" class="nav-brand">
          <div class="brand-icon">
            <svg viewBox="0 0 24 24" width="32" height="32">
              <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
            </svg>
          </div>
          <span class="brand-text">{{ $t('nav.brand') }}</span>
        </router-link>
        <div class="nav-actions">
          <div class="language-switcher">
            <button 
              @click="toggleLanguage" 
              class="language-btn"
              :title="$t('common.language')"
            >
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path fill="currentColor" d="M12.87,15.07L10.33,12.56L10.36,12.53C12.1,10.59 13.34,8.36 14.07,6H17V4H10V2H8V4H1V6H12.17C11.5,7.92 10.44,9.75 9,11.35C8.07,10.32 7.3,9.19 6.69,8H4.69C5.42,9.63 6.42,11.17 7.67,12.56L2.58,17.58L4,19L9,14L12.11,17.11L12.87,15.07M18.5,10H16.5L12,22H14L15.12,19H19.87L21,22H23L18.5,10M15.88,17L17.5,12.67L19.12,17H15.88Z"/>
              </svg>
              {{ currentLanguage === 'zh' ? 'EN' : '中' }}
            </button>
          </div>
          <router-link to="/blog" class="nav-btn">{{ $t('blog.backToBlog') }}</router-link>
          <router-link to="/pricing" class="nav-btn">{{ $t('pricing.nav.pricing') }}</router-link>
        </div>
      </div>
    </nav>

    <!-- 主要内容 -->
    <div class="main-container">
      <div v-if="!post" class="loading-state">
        <div class="loading-spinner"></div>
        <p>{{ $t('common.loading') }}</p>
      </div>

      <div v-else class="post-container">
        <!-- 文章头部 -->
        <header class="post-header">
          <div class="post-meta">
            <span class="post-category">{{ post.category }}</span>
            <div class="post-info">
              <span class="post-date">{{ $t('blog.publishedOn') }} {{ formatDate(post.publishedAt) }}</span>
              <span class="post-author">{{ $t('blog.author') }}: {{ post.author }}</span>
              <span class="post-read-time">{{ post.readTime }} min read</span>
            </div>
          </div>
          
          <h1 class="post-title">{{ getLocalizedContent(post.title) }}</h1>
          
          <div class="post-tags">
            <span class="tags-label">{{ $t('blog.tags') }}:</span>
            <div class="tags-list">
              <span 
                v-for="tag in post.tags" 
                :key="tag"
                class="tag"
              >
                {{ tag }}
              </span>
            </div>
          </div>
        </header>

        <!-- 文章内容 -->
        <article class="post-content">
          <div class="markdown-content" v-html="renderedContent"></div>
        </article>

        <!-- 文章底部 -->
        <footer class="post-footer">
          <div class="back-to-blog">
            <router-link to="/blog" class="back-btn">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path fill="currentColor" d="M20,11V13H8L13.5,18.5L12.08,19.92L4.16,12L12.08,4.08L13.5,5.5L8,11H20Z"/>
              </svg>
              {{ $t('blog.backToBlog') }}
            </router-link>
          </div>
        </footer>
      </div>
    </div>

    <!-- 页脚 -->
    <footer class="site-footer">
      <div class="footer-container">
        <div class="footer-brand">
          <div class="brand-icon">
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
            </svg>
          </div>
          <span>{{ $t('footer.brand') }}</span>
        </div>
        <div class="footer-links">
          <router-link to="/blog">{{ $t('footer.docs') }}</router-link>
          <a href="#">{{ $t('footer.about') }}</a>
          <a href="mailto:fn9170@gmail.com">{{ $t('footer.contact') }}</a>
        </div>
        <div class="footer-copyright">
          {{ $t('footer.copyright') }}
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { changeLanguage, getCurrentLanguage } from '@/i18n'
import blogManager from '@/utils/blogManager'
import markdownRenderer from '@/utils/markdownRenderer'

export default {
  name: 'BlogPostPage',
  data() {
    return {
      currentLanguage: getCurrentLanguage(),
      post: null
    }
  },
  computed: {
    renderedContent() {
      if (!this.post) return ''
      
      const content = this.getLocalizedContent(this.post.content)
      // 简单的 Markdown 渲染
      return this.renderMarkdown(content)
    }
  },
  methods: {
    toggleLanguage() {
      const newLanguage = this.currentLanguage === 'en' ? 'zh' : 'en'
      this.currentLanguage = changeLanguage(newLanguage)
      this.updateSEO()
    },
    
    getLocalizedContent(content) {
      return blogManager.getLocalizedContent(content, this.currentLanguage)
    },
    
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString(this.currentLanguage === 'zh' ? 'zh-CN' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    
    renderMarkdown(content) {
      return markdownRenderer.render(content)
    },
    
    updateSEO() {
      if (!this.post) return
      
      const title = this.getLocalizedContent(this.post.title)
      const excerpt = this.getLocalizedContent(this.post.excerpt)
      
      document.title = `${title} - ${this.$t('nav.brand')}`
      
      const updateMetaTag = (name, content) => {
        let element = document.querySelector(`meta[name="${name}"]`)
        if (!element) {
          element = document.createElement('meta')
          element.setAttribute('name', name)
          document.head.appendChild(element)
        }
        element.setAttribute('content', content)
      }
      
      updateMetaTag('description', excerpt)
      updateMetaTag('keywords', this.post.tags.join(', '))
    }
  },
  
  mounted() {
    const postId = this.$route.params.id
    this.post = blogManager.getPostById(postId)
    
    if (!this.post) {
      this.$router.push('/blog')
      return
    }
    
    this.updateSEO()
  },
  
  watch: {
    '$i18n.locale'() {
      this.updateSEO()
    },
    
    '$route'(to) {
      const postId = to.params.id
      this.post = blogManager.getPostById(postId)
      
      if (!this.post) {
        this.$router.push('/blog')
        return
      }
      
      this.updateSEO()
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.blog-post-page {
  min-height: 100vh;
  background: #0f172a;
  position: relative;
  overflow-x: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* ===== 动态背景 ===== */
.animated-background {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  background: 
    radial-gradient(circle at 20% 10%, rgba(59, 130, 246, 0.15) 0%, transparent 50%),
    radial-gradient(circle at 80% 90%, rgba(139, 92, 246, 0.1) 0%, transparent 50%),
    linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
}

.floating-shapes {
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.shape {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(45deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1));
  filter: blur(40px);
  animation: float 20s ease-in-out infinite;
}

.shape-1 {
  width: 300px;
  height: 300px;
  top: 10%;
  left: 10%;
  animation-delay: 0s;
}

.shape-2 {
  width: 200px;
  height: 200px;
  top: 60%;
  right: 10%;
  animation-delay: 5s;
}

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-20px) rotate(120deg); }
  66% { transform: translateY(20px) rotate(240deg); }
}

.grid-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px);
  background-size: 50px 50px;
  opacity: 0.3;
  animation: gridMove 40s linear infinite;
}

@keyframes gridMove {
  0% { transform: translate(0, 0); }
  100% { transform: translate(50px, 50px); }
}

/* ===== 导航栏 ===== */
.top-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(15, 23, 42, 0.8);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(59, 130, 246, 0.1);
  padding: 1rem 0;
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
}

.brand-icon {
  color: #3b82f6;
  transition: transform 0.3s ease;
}

.brand-icon:hover {
  transform: scale(1.1);
}

.brand-text {
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.nav-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.language-switcher {
  position: relative;
}

.language-btn {
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  color: #e2e8f0;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  min-width: 60px;
  justify-content: center;
}

.language-btn:hover {
  background: rgba(59, 130, 246, 0.2);
  border-color: #3b82f6;
  transform: translateY(-1px);
}

.nav-btn {
  background: transparent;
  border: 1px solid rgba(59, 130, 246, 0.3);
  color: #e2e8f0;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none !important;
}

.nav-btn:hover {
  background: rgba(59, 130, 246, 0.1);
  border-color: #3b82f6;
}

/* ===== 主容器 ===== */
.main-container {
  position: relative;
  z-index: 1;
  padding-top: 100px;
  min-height: calc(100vh - 100px);
}

/* ===== 加载状态 ===== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: #94a3b8;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(59, 130, 246, 0.3);
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ===== 文章容器 ===== */
.post-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

/* ===== 文章头部 ===== */
.post-header {
  margin-bottom: 3rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid rgba(59, 130, 246, 0.1);
}

.post-meta {
  margin-bottom: 2rem;
}

.post-category {
  background: rgba(59, 130, 246, 0.2);
  color: #3b82f6;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  display: inline-block;
  margin-bottom: 1rem;
}

.post-info {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  color: #64748b;
  font-size: 0.9rem;
}

.post-title {
  color: #f1f5f9;
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 2rem;
  letter-spacing: -0.02em;
}

.post-tags {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.tags-label {
  color: #94a3b8;
  font-weight: 600;
}

.tags-list {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.tag {
  background: rgba(139, 92, 246, 0.1);
  color: #8b5cf6;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

/* ===== 文章内容 ===== */
.post-content {
  margin-bottom: 3rem;
}

.markdown-content {
  color: #e2e8f0;
  line-height: 1.8;
  font-size: 1.1rem;
}

.markdown-content h1 {
  color: #f1f5f9;
  font-size: 2rem;
  font-weight: 700;
  margin: 2rem 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid rgba(59, 130, 246, 0.3);
}

.markdown-content h2 {
  color: #f1f5f9;
  font-size: 1.6rem;
  font-weight: 600;
  margin: 1.5rem 0 1rem 0;
}

.markdown-content h3 {
  color: #f1f5f9;
  font-size: 1.3rem;
  font-weight: 600;
  margin: 1.5rem 0 0.75rem 0;
}

.markdown-content p {
  margin-bottom: 1.5rem;
}

.markdown-content ul {
  margin: 1rem 0;
  padding-left: 2rem;
}

.markdown-content li {
  margin-bottom: 0.5rem;
  color: #cbd5e1;
}

.markdown-content strong {
  color: #f1f5f9;
  font-weight: 600;
}

.markdown-content em {
  color: #94a3b8;
  font-style: italic;
}

.markdown-content code {
  background: rgba(30, 41, 59, 0.8);
  color: #06b6d4;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
  font-size: 0.9em;
}

.markdown-content pre {
  background: rgba(15, 23, 42, 0.9);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 12px;
  padding: 1.5rem;
  margin: 2rem 0;
  overflow-x: auto;
}

.markdown-content pre code {
  background: none;
  padding: 0;
  color: #e2e8f0;
  font-size: 0.9rem;
  line-height: 1.6;
}

.markdown-content a {
  color: #3b82f6;
  text-decoration: none;
  border-bottom: 1px solid rgba(59, 130, 246, 0.3);
  transition: all 0.3s ease;
}

.markdown-content a:hover {
  color: #60a5fa;
  border-bottom-color: #60a5fa;
}

/* ===== 文章底部 ===== */
.post-footer {
  padding-top: 2rem;
  border-top: 1px solid rgba(59, 130, 246, 0.1);
}

.back-to-blog {
  text-align: center;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  color: #3b82f6;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
}

.back-btn:hover {
  background: rgba(59, 130, 246, 0.2);
  border-color: #3b82f6;
  transform: translateY(-2px);
}

/* ===== 页脚 ===== */
.site-footer {
  background: rgba(15, 23, 42, 0.9);
  border-top: 1px solid rgba(59, 130, 246, 0.1);
  padding: 3rem 2rem 2rem;
  position: relative;
  z-index: 1;
  margin-top: 4rem;
}

.footer-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 2rem;
}

.footer-brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #f1f5f9;
  font-weight: 600;
}

.footer-brand .brand-icon {
  color: #3b82f6;
}

.footer-links {
  display: flex;
  gap: 2rem;
}

.footer-links a {
  color: #94a3b8;
  text-decoration: none;
  transition: color 0.3s ease;
}

.footer-links a:hover {
  color: #3b82f6;
}

.footer-copyright {
  color: #64748b;
  font-size: 0.9rem;
}

/* ===== 响应式设计 ===== */
@media (max-width: 768px) {
  .nav-container {
    padding: 0 1rem;
  }
  
  .post-container {
    padding: 1rem;
  }
  
  .post-title {
    font-size: 2rem;
  }
  
  .post-info {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .post-tags {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .markdown-content {
    font-size: 1rem;
  }
  
  .markdown-content h1 {
    font-size: 1.6rem;
  }
  
  .markdown-content h2 {
    font-size: 1.4rem;
  }
  
  .markdown-content h3 {
    font-size: 1.2rem;
  }
  
  .footer-container {
    flex-direction: column;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .post-title {
    font-size: 1.8rem;
  }
  
  .markdown-content pre {
    padding: 1rem;
    margin: 1rem 0;
  }
}
</style>